import { computed, makeObservable, observable, action } from 'mobx';
import React from 'react';
import { Resource } from 'src_common/common/mobx-utils/Resource';
import { getRollingLimits, putRollingLimits, getDepositLimits } from 'src_server/trpc/types/responsibleGambling';
import {
    RollingNetDepositLimitItemState,
    getAccountDepositLimitPeriodSelectOptions,
} from 'src/domains/players/webview/components/Account/limitsTab/rollingNetDepositLimit/RollingNetDepositLimitItem';
import { ConfigComponents } from 'src/domains/layouts/config/features/config';
import { Common } from 'src/domains/common/Common';
import {
    rollingLimitCombinationKey,
    UsersState,
    RollingNetDepositLimitDataType,
} from 'src/domains/players/state/UsersState';
import { BasicDataModel } from 'src/domains/players/state/BasicDataModel';
import { LanguagesState } from 'src/domains/layouts/state/languagesState/LanguagesState';
import { Amount } from 'src_common/common/amount/Amount';
import { SelectState } from 'src/domains/players/webview/components/form/select/Select.state';
import { SelectOptionType } from 'src/domains/players/webview/components/form/select/Select';
import { FormInputState } from 'src_common/common/mobx-utils/Form2/FormInputState';
import { validateAmountRequire } from 'src/domains/players/webview/components/ValidatorsNew';
import { DateTime } from 'src_common/utils/time/time';
import {
    isGrossToNetLimitChange,
    resolveAccountLimitWindowType,
    resolveAccountEditCalculationType,
    resolveAccountEditLimitType,
    resolvePutRollingLimitType,
    type DepositLimitWindowType,
} from 'src/domains/players/webview/components/Account/limitsTab/rollingNetDepositLimit/depositLimitType';

export type PendingNewLimitSuccessContextType = {
    pendingLimitValue: number;
    pendingRequestedAt: string;
};

const usedDepositLimit = (
    depositLimits: getDepositLimits.SuccessResponseType['limits'] | undefined,
    limitDayNumber: 1 | 7 | 30,
    calculationType: getRollingLimits.CalculationTypeSchemaType,
    limitType: getRollingLimits.LimitsObjectSchemaType['limitType'] | undefined
):
    | {
          used: number;
          limitType: Exclude<getDepositLimits.SuccessResponseType['limits'][number]['type'], 'lifetime'>;
      }
    | undefined => {
    const response = depositLimits?.find((limitItem) => {
        if (limitItem.windowLengthInDays !== limitDayNumber) {
            return false;
        }
        if (limitItem.type === 'lifetime') {
            return false;
        }
        if (limitItem.calculationType !== calculationType) {
            return false;
        }
        if (limitType !== undefined && limitItem.type !== limitType) {
            return false;
        }
        return true;
    });
    if (response === undefined) return undefined;
    if (response.type === 'lifetime') return undefined;

    return {
        used: response.used,
        limitType: response.type,
    };
};

export type InfoMessageType =
    | 'success'
    | 'error'
    | 'empty'
    | 'belowOne'
    | 'grossToNetNotAllowed'
    | 'amountTypeMismatch';
export type RollingPayloadListType = putRollingLimits.InputType['payload']['list'];
interface ConvertedLimitsType {
    [key: string]: Amount | undefined;
}
interface ChekIsDepositLimitAlreadySetType {
    newValueWhenDiffer: Record<number, Amount | undefined>;
}

type SingleEditPlanType = {
    payload: putRollingLimits.LimitObjectType | null;
    pendingLimit: PendingNewLimitSuccessContextType | null;
    errorMessage: InfoMessageType | null;
    isEmptyInput: boolean;
};

export const preparePayload = (
    payload: RollingPayloadListType,
    currentLimits: getRollingLimits.SuccessResponseType
): RollingPayloadListType => {
    const preparedPayload: RollingPayloadListType = [];
    for (const limit of payload) {
        const foundLimit =
            currentLimits.limits.find(
                (l) =>
                    l.windowLengthInDays === limit.windowLengthInDays &&
                    (l.calculationType ?? 'net') === limit.calculationType
            ) ?? null;

        if (foundLimit === null) {
            preparedPayload.push(limit);
        } else {
            preparedPayload.push({ ...limit, limitValueMax: foundLimit.limitValueMax });
        }
    }
    return preparedPayload;
};

type DepositLimitPeriodItemsType = {
    daily: RollingNetDepositLimitItemState;
    weekly: RollingNetDepositLimitItemState;
    monthly: RollingNetDepositLimitItemState;
};

export class RollingNetDepositLimitState {
    private periodItems!: Record<getRollingLimits.CalculationTypeSchemaType, DepositLimitPeriodItemsType>;

    public readonly getDepositLimitsData: Resource<getDepositLimits.ResponseType>;
    public readonly periodSelectState: SelectState;
    public readonly editLimitAmount: FormInputState<string, Amount | undefined>;

    @observable public infoMessage: InfoMessageType | null = null;
    @observable private lastSubmittedPendingLimit: PendingNewLimitSuccessContextType | null = null;
    @observable private isDisableButton = false;
    @observable public selectedEditWindow: 1 | 7 | 30 = 1;

    @observable public calculationType: getRollingLimits.CalculationTypeSchemaType | undefined = undefined;

    public constructor(
        private readonly common: Common,
        private readonly configComponents: ConfigComponents,
        private readonly usersState: UsersState,
        private readonly languagesState: LanguagesState,
        private readonly onSuccessfulDepositLimitSave: () => void,
        initialCalculationType: getRollingLimits.CalculationTypeSchemaType = 'gross'
    ) {
        makeObservable(this);
        this.calculationType = initialCalculationType;

        const periodOptions = getAccountDepositLimitPeriodSelectOptions(
            this.languagesState,
            this.periodSelectLimitTypes(this.calculationType)
        );
        const firstPeriod =
            periodOptions[0] ??
            ({
                '1': this.languagesState.getTranslation('account.net-deposit-limit.label.oneDay', '1 Day'),
            } satisfies SelectOptionType);
        this.periodSelectState = new SelectState(firstPeriod, true);
        this.editLimitAmount = FormInputState.new('').map(validateAmountRequire);

        this.getDepositLimitsData = new Resource(async (): Promise<getDepositLimits.ResponseType> => {
            const response = await this.common.trpcClient.client.responsibleGamblingRouter.getDepositLimits.query();
            return response;
        });

        const createPeriodItems = (
            calculationType: getRollingLimits.CalculationTypeSchemaType
        ): DepositLimitPeriodItemsType => ({
            daily: new RollingNetDepositLimitItemState(
                this.configComponents,
                this.usersState,
                this.clearInfoMessage,
                () => this.getLimitFor(1, calculationType),
                () =>
                    usedDepositLimit(
                        this.getDepositLimits?.limits,
                        1,
                        calculationType,
                        this.getLimitFor(1, calculationType)?.limitType
                    )?.used ?? 0
            ),
            weekly: new RollingNetDepositLimitItemState(
                this.configComponents,
                this.usersState,
                this.clearInfoMessage,
                () => this.getLimitFor(7, calculationType),
                () =>
                    usedDepositLimit(
                        this.getDepositLimits?.limits,
                        7,
                        calculationType,
                        this.getLimitFor(7, calculationType)?.limitType
                    )?.used ?? 0
            ),
            monthly: new RollingNetDepositLimitItemState(
                this.configComponents,
                this.usersState,
                this.clearInfoMessage,
                () => this.getLimitFor(30, calculationType),
                () =>
                    usedDepositLimit(
                        this.getDepositLimits?.limits,
                        30,
                        calculationType,
                        this.getLimitFor(30, calculationType)?.limitType
                    )?.used ?? 0
            ),
        });

        this.periodItems = {
            gross: createPeriodItems('gross'),
            net: createPeriodItems('net'),
        };
    }

    @computed public get daily(): RollingNetDepositLimitItemState {
        return this.limitsFor(this.calculationType ?? 'gross').daily;
    }

    @computed public get weekly(): RollingNetDepositLimitItemState {
        return this.limitsFor(this.calculationType ?? 'gross').weekly;
    }

    @computed public get monthly(): RollingNetDepositLimitItemState {
        return this.limitsFor(this.calculationType ?? 'gross').monthly;
    }

    public limitsFor(calculationType: getRollingLimits.CalculationTypeSchemaType): DepositLimitPeriodItemsType {
        return this.periodItems[calculationType];
    }

    @computed public get editMaxLimitFormatted(): string {
        const limitValueMax = this.getItemForWindow(this.selectedEditWindow).getLimits()?.limitValueMax;
        const currency = BasicDataModel.get(this.common).currency;
        return limitValueMax === null || limitValueMax === undefined
            ? this.configComponents.precision.newFromAnything(0).format(currency)
            : this.configComponents.precision.newFromOld(limitValueMax).format(currency);
    }

    @computed public get pendingNewLimitSuccessContext(): PendingNewLimitSuccessContextType | null {
        if (this.infoMessage !== 'success') {
            return null;
        }

        const limits = this.getItemForWindow(this.selectedEditWindow).getLimits();
        const serverPendingLimitValue = limits?.pendingLimitValue;
        const serverPendingRequestedAt = limits?.pendingRequestedAt;
        const hasServerPending =
            limits?.skipPendingState !== true &&
            serverPendingLimitValue != null &&
            serverPendingLimitValue !== 0 &&
            serverPendingRequestedAt != null &&
            serverPendingRequestedAt !== '';

        if (hasServerPending) {
            return {
                pendingLimitValue: serverPendingLimitValue,
                pendingRequestedAt: serverPendingRequestedAt,
            };
        }

        return this.lastSubmittedPendingLimit;
    }

    @computed public get editAmountExceedsMax(): boolean {
        const limitValueMax = this.getItemForWindow(this.selectedEditWindow).getLimits()?.limitValueMax ?? null;
        const currentValue =
            this.editLimitAmount.result.value.type === 'ok'
                ? this.editLimitAmount.result.value.data?.multiply(100)
                : undefined;
        return limitValueMax !== null && limitValueMax < parseInt(currentValue?.value ?? '0', 10);
    }

    private clearInfoMessage = (): void => {
        this.infoMessage = null;
        this.lastSubmittedPendingLimit = null;
    };

    private clearInputs = (): void => {
        this.daily.resetValue();
        this.weekly.resetValue();
        this.monthly.resetValue();
    };

    private getLimitFor(
        windowLengthInDays: 1 | 7 | 30,
        calculationType: getRollingLimits.CalculationTypeSchemaType
    ): getRollingLimits.LimitsObjectSchemaType | undefined {
        const data = this.usersState.rollingNetDepositLimitData.valueReady;
        if (data === null) {
            return undefined;
        }

        if (
            this.configComponents.config.depositLimitsPreferCalendarNetOnDuplicate === true &&
            calculationType === 'net'
        ) {
            const duplicateNetLimits = this.getDuplicateNetLimits(windowLengthInDays, data);
            if (duplicateNetLimits !== null) {
                return duplicateNetLimits.calendar;
            }
        }

        const preferredLimitType = resolvePutRollingLimitType(
            windowLengthInDays,
            calculationType,
            this.usersState.depositLimitConfigData.valueReady ?? undefined
        );
        const preferred = data.byWindowCalculationAndLimitType.get(
            rollingLimitCombinationKey(windowLengthInDays, calculationType, preferredLimitType)
        );
        if (preferred !== undefined) {
            return preferred;
        }

        const fallbackLimitType = preferredLimitType === 'calendar' ? 'rolling' : 'calendar';
        const fallback = data.byWindowCalculationAndLimitType.get(
            rollingLimitCombinationKey(windowLengthInDays, calculationType, fallbackLimitType)
        );
        if (fallback !== undefined) {
            return fallback;
        }

        return data.byWindowAndType.get(`${windowLengthInDays}:${calculationType}`);
    }

    private getDuplicateNetLimits(
        windowLengthInDays: number,
        data: RollingNetDepositLimitDataType
    ): {
        calendar: getRollingLimits.LimitsObjectSchemaType;
        rolling: getRollingLimits.LimitsObjectSchemaType;
    } | null {
        const calendar = data.byWindowCalculationAndLimitType.get(
            rollingLimitCombinationKey(windowLengthInDays, 'net', 'calendar')
        );
        const rolling = data.byWindowCalculationAndLimitType.get(
            rollingLimitCombinationKey(windowLengthInDays, 'net', 'rolling')
        );
        if (calendar === undefined || rolling === undefined) {
            return null;
        }

        return { calendar, rolling };
    }

    private withPairedNetDuplicatePayload(
        payload: putRollingLimits.LimitObjectType
    ): putRollingLimits.LimitObjectType[] {
        if (
            this.configComponents.config.depositLimitsPreferCalendarNetOnDuplicate !== true ||
            payload.calculationType !== 'net'
        ) {
            return [payload];
        }

        const data = this.usersState.rollingNetDepositLimitData.valueReady;
        if (data === null) {
            return [payload];
        }

        const duplicates = this.getDuplicateNetLimits(payload.windowLengthInDays, data);
        if (duplicates === null) {
            return [payload];
        }

        const otherLimitType = payload.limitType === 'calendar' ? 'rolling' : 'calendar';
        const otherLimit = otherLimitType === 'rolling' ? duplicates.rolling : duplicates.calendar;

        return [
            payload,
            {
                windowLengthInDays: payload.windowLengthInDays,
                pendingLimitValue: payload.pendingLimitValue,
                limitValueMax: otherLimit.limitValueMax ?? null,
                triggersWarningEmail: payload.pendingLimitValue === (otherLimit.limitValueMax ?? null),
                hardLimit: otherLimit.hardLimit,
                limitType: otherLimitType,
                calculationType: 'net',
                ...(otherLimitType === 'rolling' ? { skipPendingState: true } : {}),
            },
        ];
    }

    public getItemForWindow(w: 1 | 7 | 30): RollingNetDepositLimitItemState {
        if (w === 1) return this.daily;
        if (w === 7) return this.weekly;
        return this.monthly;
    }

    @computed public get periodSelectOptions(): SelectOptionType[] {
        return getAccountDepositLimitPeriodSelectOptions(
            this.languagesState,
            this.periodSelectLimitTypes(this.calculationType)
        );
    }

    private periodSelectLimitTypes(
        calculationType: getRollingLimits.CalculationTypeSchemaType | undefined
    ): Record<1 | 7 | 30, DepositLimitWindowType> {
        return {
            1: this.effectiveLimitTypeForWindow(1, calculationType),
            7: this.effectiveLimitTypeForWindow(7, calculationType),
            30: this.effectiveLimitTypeForWindow(30, calculationType),
        };
    }

    private effectiveLimitTypeForWindow(
        windowLengthInDays: 1 | 7 | 30,
        calculationType: getRollingLimits.CalculationTypeSchemaType | undefined
    ): DepositLimitWindowType {
        const calculationTypeForLimit = calculationType ?? 'net';
        return resolveAccountLimitWindowType(
            this.getLimitFor(windowLengthInDays, calculationTypeForLimit)?.limitType,
            windowLengthInDays,
            calculationTypeForLimit,
            this.usersState.depositLimitConfigData.valueReady ?? undefined
        );
    }

    @computed public get isDisabledSubmit(): boolean {
        return (
            this.isDisableButton ||
            this.editLimitAmount.result.value.type === 'error' ||
            this.editLimitAmount.value === '' ||
            this.editAmountExceedsMax === true
        );
    }

    @computed public get isSubmitting(): boolean {
        return this.isDisableButton;
    }

    @computed public get isDisabledButton(): boolean {
        return (
            this.isDisableButton ||
            this.isErrorInput ||
            (this.daily.limitAmount.value === '' &&
                this.weekly.limitAmount.value === '' &&
                this.monthly.limitAmount.value === '')
        );
    }

    @computed private get getDepositLimits(): getDepositLimits.SuccessResponseType | undefined {
        const response = this.getDepositLimitsData.get();

        if (response.type === 'ready' && response.value.responseStatus === 'success') {
            return response.value.data;
        }
        return undefined;
    }

    @computed public get rollingNetDepositLimitStatus(): 'error' | 'loading' | 'ready' {
        const depositsModel = this.usersState.rollingNetDepositLimitData.get();
        return depositsModel.type;
    }

    @computed private get isErrorInput(): boolean {
        return this.daily.isError || this.weekly.isError || this.monthly.isError;
    }

    private syncEditAmountFromActiveLimit(): void {
        const item = this.getItemForWindow(this.selectedEditWindow);
        const active = item.getLimits()?.activeLimitValue;
        if (active === undefined) {
            this.editLimitAmount.setValue('');
            return;
        }
        const num = parseFloat(this.configComponents.precision.newFromOld(active).value);
        if (isNaN(num) === true || num === 0) {
            this.editLimitAmount.setValue('');
            return;
        }
        this.editLimitAmount.setValue(num.toFixed(2));
    }

    private getActualLimits = (): ConvertedLimitsType => {
        const getValue = (period: RollingNetDepositLimitItemState): Amount | undefined => {
            const shortenValue = period.limitAmount.result.value;
            return shortenValue.type === 'ok' ? shortenValue.data : undefined;
        };

        return {
            dailyValue: getValue(this.daily),
            weeklyValue: getValue(this.weekly),
            monthlyValue: getValue(this.monthly),
        };
    };

    private getPreviousLimits = (): ConvertedLimitsType => {
        const getPreviousLimit = (period: RollingNetDepositLimitItemState): Amount | undefined => {
            const limit = period.getLimits()?.activeLimitValue;
            return limit === undefined ? undefined : this.configComponents.precision.newFromOld(limit);
        };

        return {
            previousDaily: getPreviousLimit(this.daily),
            previousWeekly: getPreviousLimit(this.weekly),
            previousMonthly: getPreviousLimit(this.monthly),
        };
    };

    private checkIsDepositLimitAlreadySet = (): ChekIsDepositLimitAlreadySetType => {
        const { dailyValue, weeklyValue, monthlyValue } = this.getActualLimits();
        const { previousDaily, previousWeekly, previousMonthly } = this.getPreviousLimits();

        const hasValueChanged = (newValue: Amount | undefined, oldValue: Amount | undefined): boolean =>
            newValue !== undefined && oldValue?.value !== newValue.value;

        return {
            newValueWhenDiffer: {
                1: hasValueChanged(dailyValue, previousDaily) ? dailyValue : undefined,
                7: hasValueChanged(weeklyValue, previousWeekly) ? weeklyValue : undefined,
                30: hasValueChanged(monthlyValue, previousMonthly) ? monthlyValue : undefined,
            },
        };
    };

    private checkIsLessThanOne(limits: ConvertedLimitsType): boolean {
        const limit = new Amount('1');
        const values = [limits.dailyValue, limits.weeklyValue, limits.monthlyValue];

        return values.some((value) => value !== undefined && value.isLessThan(limit));
    }

    private async updateRollingLimits(
        payload: RollingPayloadListType,
        currentLimits: getRollingLimits.SuccessResponseType
    ): Promise<putRollingLimits.ResponseType> {
        const preparedPayload = preparePayload(payload, currentLimits);

        const response = await this.common.trpcClient.client.responsibleGamblingRouter.putRollingLimits.mutate({
            payload: {
                source: 'website',
                list: preparedPayload,
            },
        });
        return response;
    }

    private setTriggersWarningEmail = (limitValueMax: number | null, pendingLimitValue: number): boolean => {
        return pendingLimitValue === limitValueMax ? true : false;
    };

    private async updateLimits(): Promise<void> {
        const daysArray = [1, 7, 30] as const;
        const preparedPayload: putRollingLimits.LimitObjectType[] = [];
        daysArray.forEach((days) => {
            const newValue = this.checkIsDepositLimitAlreadySet().newValueWhenDiffer[days];
            const windowDays = days as 1 | 7 | 30;
            const calculationType = this.calculationType;

            if (newValue !== undefined && calculationType !== undefined) {
                const previousLimitData = this.getLimitFor(windowDays, calculationType);
                const calculationTypeToSave = resolveAccountEditCalculationType(
                    previousLimitData?.calculationType,
                    calculationType
                );
                const pendingLimitValue = parseInt(newValue.multiply(100).value, 10);
                const triggersWarningEmail = this.setTriggersWarningEmail(
                    previousLimitData?.limitValueMax ?? null,
                    pendingLimitValue
                );

                preparedPayload.push({
                    windowLengthInDays: days,
                    pendingLimitValue,
                    limitValueMax: previousLimitData?.limitValueMax ?? null,
                    triggersWarningEmail,
                    hardLimit: previousLimitData?.hardLimit ?? false,
                    limitType: resolveAccountEditLimitType(
                        previousLimitData?.limitType,
                        windowDays,
                        calculationTypeToSave,
                        this.usersState.depositLimitConfigData.valueReady ?? undefined
                    ),
                    calculationType: calculationTypeToSave,
                });
            }
        });

        const currentLimits = await this.common.trpcClient.client.responsibleGamblingRouter.getRollingLimits.query();

        if (currentLimits.responseStatus === 'success') {
            const response = await this.updateRollingLimits(preparedPayload, currentLimits.data);
            this.infoMessage = response.responseStatus === 'success' ? 'success' : 'error';

            if (response.responseStatus === 'success') {
                this.usersState.rollingNetDepositLimitData.refresh();
                await this.getDepositLimitsData.refresh();
            }
        } else {
            this.infoMessage = 'error';
        }
    }

    private createSingleEditPlan(): SingleEditPlanType {
        if (this.editAmountExceedsMax === true) {
            return {
                payload: null,
                pendingLimit: null,
                errorMessage: null,
                isEmptyInput: false,
            };
        }

        const newValueResult = this.editLimitAmount.result.value;
        if (this.editLimitAmount.value.trim() === '') {
            return {
                payload: null,
                pendingLimit: null,
                errorMessage: null,
                isEmptyInput: true,
            };
        }
        if (newValueResult.type !== 'ok' || newValueResult.data === undefined) {
            return {
                payload: null,
                pendingLimit: null,
                errorMessage: 'empty',
                isEmptyInput: false,
            };
        }

        const newValue = newValueResult.data;
        if (newValue.isLessThan(new Amount('1'))) {
            return {
                payload: null,
                pendingLimit: null,
                errorMessage: 'belowOne',
                isEmptyInput: false,
            };
        }

        if (this.calculationType === undefined) {
            return {
                payload: null,
                pendingLimit: null,
                errorMessage: 'amountTypeMismatch',
                isEmptyInput: false,
            };
        }

        const days = this.selectedEditWindow;
        const selectedCalculationType = this.calculationType;
        const previousLimitData = this.getLimitFor(days, selectedCalculationType);
        const storedCalculationType = previousLimitData?.calculationType;
        if (isGrossToNetLimitChange(storedCalculationType, selectedCalculationType)) {
            return {
                payload: null,
                pendingLimit: null,
                errorMessage: 'grossToNetNotAllowed',
                isEmptyInput: false,
            };
        }
        const calculationTypeToSave = resolveAccountEditCalculationType(storedCalculationType, selectedCalculationType);
        const previousCalculationType = storedCalculationType ?? 'net';

        const oldVal =
            previousLimitData?.activeLimitValue === undefined
                ? undefined
                : this.configComponents.precision.newFromOld(previousLimitData.activeLimitValue);
        if (
            oldVal !== undefined &&
            oldVal.value === newValue.value &&
            previousCalculationType === calculationTypeToSave
        ) {
            return {
                payload: null,
                pendingLimit: null,
                errorMessage: null,
                isEmptyInput: false,
            };
        }

        const pendingLimitValue = parseInt(newValue.multiply(100).value, 10);
        const triggersWarningEmail = this.setTriggersWarningEmail(
            previousLimitData?.limitValueMax ?? null,
            pendingLimitValue
        );

        return {
            payload: {
                windowLengthInDays: days,
                pendingLimitValue,
                limitValueMax: previousLimitData?.limitValueMax ?? null,
                triggersWarningEmail,
                hardLimit: previousLimitData?.hardLimit ?? false,
                limitType: resolveAccountEditLimitType(
                    previousLimitData?.limitType,
                    days,
                    calculationTypeToSave,
                    this.usersState.depositLimitConfigData.valueReady ?? undefined
                ),
                calculationType: calculationTypeToSave,
            },
            pendingLimit: this.shouldApplyCoolingOff(previousLimitData, pendingLimitValue)
                ? {
                      pendingLimitValue,
                      pendingRequestedAt: DateTime.current().toISOString(),
                  }
                : null,
            errorMessage: null,
            isEmptyInput: false,
        };
    }

    private async submitSingleEditLimit(): Promise<void> {
        const plan = this.createSingleEditPlan();
        if (plan.isEmptyInput === true) {
            this.infoMessage = 'empty';
            return;
        }
        if (plan.errorMessage !== null) {
            this.infoMessage = plan.errorMessage;
            return;
        }
        if (plan.payload === null) {
            this.editLimitAmount.reset();
            this.onSuccessfulDepositLimitSave();
            return;
        }

        if (this.configComponents.config.depositLimitsPreferCalendarNetOnDuplicate === true) {
            const payloadsToSave = this.withPairedNetDuplicatePayload(plan.payload);
            if (payloadsToSave.length > 1) {
                const didSaveAllPayloads = await this.saveCombinedPayloads(payloadsToSave);
                if (didSaveAllPayloads === false) {
                    return;
                }

                this.infoMessage = 'success';
                this.lastSubmittedPendingLimit = plan.pendingLimit;
                await this.usersState.rollingNetDepositLimitData.refreshAndWait();
                await this.getDepositLimitsData.refresh();
                this.editLimitAmount.reset();
                this.onSuccessfulDepositLimitSave();
                return;
            }
        }

        const currentLimits = await this.common.trpcClient.client.responsibleGamblingRouter.getRollingLimits.query();

        if (currentLimits.responseStatus === 'success') {
            const response = await this.updateRollingLimits([plan.payload], currentLimits.data);
            this.infoMessage = response.responseStatus === 'success' ? 'success' : 'error';

            if (response.responseStatus === 'success') {
                this.lastSubmittedPendingLimit = plan.pendingLimit;
                await this.usersState.rollingNetDepositLimitData.refreshAndWait();
                await this.getDepositLimitsData.refresh();
                this.editLimitAmount.reset();
                this.onSuccessfulDepositLimitSave();
            }
        } else {
            this.infoMessage = 'error';
        }
    }

    private applyCombinedPlanValidation(
        plans: readonly SingleEditPlanType[],
        states: readonly RollingNetDepositLimitState[]
    ): boolean {
        plans.forEach((plan, index) => {
            const state = states[index];
            if (state === undefined) {
                return;
            }
            if (plan.errorMessage !== null) {
                state.infoMessage = plan.errorMessage;
                return;
            }
            if (plan.isEmptyInput === true) {
                // Empty inputs are handled after we inspect both plans.
                state.clearInfoMessage();
                return;
            }
            if (plan.payload === null) {
                state.clearInfoMessage();
                return;
            }
            state.clearInfoMessage();
        });

        return plans.some((plan) => plan.errorMessage !== null);
    }

    private async saveCombinedPayloads(payloads: readonly putRollingLimits.LimitObjectType[]): Promise<boolean> {
        // The limits API matches an update by window length only. Weekly and 7 Days are both 7,
        // so one request keeps a single type. Save each type on its own.
        for (const payload of payloads) {
            const currentLimits =
                await this.common.trpcClient.client.responsibleGamblingRouter.getRollingLimits.query();

            if (currentLimits.responseStatus !== 'success') {
                this.infoMessage = 'error';
                return false;
            }

            const response = await this.updateRollingLimits([payload], currentLimits.data);

            if (response.responseStatus !== 'success') {
                this.infoMessage = 'error';
                return false;
            }
        }

        return true;
    }

    private async finalizeCombinedSubmit(pairedState: RollingNetDepositLimitState): Promise<void> {
        await this.usersState.rollingNetDepositLimitData.refreshAndWait();
        await this.getDepositLimitsData.refresh();
        await pairedState.getDepositLimitsData.refresh();
        this.editLimitAmount.reset();
        pairedState.editLimitAmount.reset();
        this.onSuccessfulDepositLimitSave();
    }

    @action public submitCombinedEdits = async (pairedState: RollingNetDepositLimitState): Promise<void> => {
        if (this.isDisableButton === true || pairedState.isDisableButton === true) {
            return;
        }

        const plans = [this.createSingleEditPlan(), pairedState.createSingleEditPlan()];
        const states = [this, pairedState];

        if (this.applyCombinedPlanValidation(plans, states)) {
            return;
        }

        const payloads = plans.flatMap((plan) => (plan.payload === null ? [] : [plan.payload]));
        const payloadsToSave =
            this.configComponents.config.depositLimitsPreferCalendarNetOnDuplicate === true
                ? payloads.flatMap((payload) => this.withPairedNetDuplicatePayload(payload))
                : payloads;
        if (payloads.length === 0) {
            return;
        }

        this.isDisableButton = true;
        pairedState.isDisableButton = true;

        try {
            const didSaveAllPayloads = await this.saveCombinedPayloads(payloadsToSave);
            if (didSaveAllPayloads === false) {
                return;
            }

            await this.finalizeCombinedSubmit(pairedState);
        } catch (error) {
            console.log(error);
            this.infoMessage = 'error';
        } finally {
            this.isDisableButton = false;
            pairedState.isDisableButton = false;
        }
    };

    @action public initializeEditFormFromServer = (): void => {
        this.clearInfoMessage();
        const firstOption = this.periodSelectOptions[0];
        if (firstOption !== undefined) {
            const key = Object.keys(firstOption)[0];
            const days = parseInt(key ?? '1', 10) as 1 | 7 | 30;
            this.selectedEditWindow = days === 7 || days === 30 ? days : 1;
            this.periodSelectState.setCurrentValue(firstOption);
        }
        if (this.calculationType === undefined) {
            this.calculationType = 'gross';
        }
        this.syncPeriodSelectLabelForCurrentWindow();
        this.syncEditAmountFromActiveLimit();
    };

    @action public onPeriodSelectCommitted = (): void => {
        const opt = this.periodSelectState.value;
        if (opt === undefined) {
            return;
        }
        const key = Object.keys(opt)[0];
        const parsed = parseInt(key ?? '1', 10);
        this.selectedEditWindow = parsed === 7 ? 7 : parsed === 30 ? 30 : 1;
        this.clearInfoMessage();
        this.syncPeriodSelectLabelForCurrentWindow();
        this.syncEditAmountFromActiveLimit();
    };

    @action public selectCalculationType = (value: getRollingLimits.CalculationTypeSchemaType): void => {
        this.calculationType = value;
        this.syncPeriodSelectLabelForCurrentWindow();
        this.syncEditAmountFromActiveLimit();
        this.clearInfoMessage();
    };

    private syncPeriodSelectLabelForCurrentWindow(): void {
        const matchingOption = this.periodSelectOptions.find((option) => {
            const key = Object.keys(option)[0];
            return parseInt(key ?? '1', 10) === this.selectedEditWindow;
        });
        if (matchingOption !== undefined) {
            this.periodSelectState.setCurrentValue(matchingOption);
        }
    }

    @action public onEditLimitChange = (event: React.SyntheticEvent<HTMLInputElement>): void => {
        this.clearInfoMessage();
        this.editLimitAmount.setValue(event.currentTarget.value);
    };

    @action public onEditLimitBlur = (): void => {
        const amount = parseFloat(this.editLimitAmount.value);
        if (isNaN(amount) === true || amount === 0) {
            this.editLimitAmount.setValue('');
            return;
        }
        this.editLimitAmount.setValue(amount.toFixed(2));
    };

    @action public onSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
        if (this.isDisableButton === true) {
            console.warn('action is executed');
            return;
        }

        e.preventDefault();

        const { dailyValue, weeklyValue, monthlyValue } = this.getActualLimits();

        if (dailyValue === undefined && weeklyValue === undefined && monthlyValue === undefined) {
            this.clearInputs();
            this.infoMessage = 'empty';
            return;
        }

        if (this.checkIsLessThanOne({ dailyValue, weeklyValue, monthlyValue })) {
            this.clearInputs();
            this.infoMessage = 'belowOne';
            return;
        }

        try {
            this.isDisableButton = true;
            await this.updateLimits();

            this.isDisableButton = false;
        } catch (error) {
            console.log(error);
            this.infoMessage = 'error';
            this.isDisableButton = false;
        }
    };

    private shouldApplyCoolingOff(
        previousLimitData: getRollingLimits.LimitsObjectSchemaType | undefined,
        pendingLimitValue: number
    ): boolean {
        const previousActiveLimit = previousLimitData?.activeLimitValue;
        if (previousActiveLimit === undefined) {
            return false;
        }

        return pendingLimitValue > previousActiveLimit;
    }

    @action public onSubmitEdit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
        e.preventDefault();
        if (this.isDisabledSubmit === true) {
            return;
        }

        try {
            this.isDisableButton = true;
            await this.submitSingleEditLimit();
            this.isDisableButton = false;
        } catch (error) {
            console.log(error);
            this.infoMessage = 'error';
            this.isDisableButton = false;
        }
    };
}
